import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Building2,
  ArrowLeft,
  RefreshCw,
  Users,
  GraduationCap,
  Search,
  Eye,
  Calendar,
  X
} from 'lucide-react';
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-dt/css/dataTables.dataTables.css';
import './datatables-custom.css';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import HeroCarousel from '../../../../components/carousel/HeroCarousel';
import KVSLoader from '../../../../components/loaders/KVSLoader';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import img1 from '../../../../assets/attendenceCarousel.png';
import { attendanceApi } from '../../services/api';
import { DateRangePicker } from '../../components/analytics/DateRangePicker';

DataTable.use(DT);

interface AttendanceDetail {
  attendanceDate: string;
  attendanceStatus: 'Present' | 'Absent' | 'Not Marked';
}

interface Student {
  studentID: string;
  uuid: string;
  academicSession: string;
  ubiID: string;
  admissionNo: string;
  roID: string;
  kvID: string;
  class: number;
  section: string;
  stream: number;
  rollNo: number;
  studentCode: string | null;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string | null;
  gender: string;
  dob: string;
  bloodGroup: string;
  apaarNo: string;
  admissionGround: string;
  admissionCategory: string;
  parentServiceCategory: string;
  motherTongue: string;
  socialCategory: string;
  minorityGroup: string;
  bplBeneficiary: string;
  cwsn: string;
  rte: any;
  sgc: string;
  kvsWard: any;
  nationality?: string;
  photo?: string | null;
  schoolName?: string;
  roName?: string;
  stateName?: string;
  attendancedetails?: AttendanceDetail[];
}

// Attendance Modal Component
const AttendanceModal: React.FC<{
  student: Student;
  onClose: () => void;
}> = ({ student, onClose }) => {
  const navigate = useNavigate();
  const attendanceDetails = student.attendancedetails || [];
  
  // Calculate stats
  const presentCount = attendanceDetails.filter(a => a.attendanceStatus === 'Present').length;
  const absentCount = attendanceDetails.filter(a => a.attendanceStatus === 'Absent').length;
  const notMarkedCount = attendanceDetails.filter(a => a.attendanceStatus === 'Not Marked').length;
  const markedDays = presentCount + absentCount;
  const attendancePercent = markedDays > 0 ? Math.round((presentCount / markedDays) * 100) : 0;

  // Helper function to format gender
  const formatGender = (gender: string) => {
    if (gender === '1') return 'Male';
    if (gender === '2') return 'Female';
    return 'Not Specified';
  };

  // Navigate to full details page
  const handleViewFullDetails = () => {
    navigate(`/student-attendance-register?studentid=${student.studentID}`);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-purple-600 to-purple-700 text-white">
          <div>
            <h3 className="text-lg font-semibold">
              {student.firstName} {student.middleName ? student.middleName + ' ' : ''}{student.lastName}
            </h3>
            <p className="text-sm text-purple-100">Roll No: {student.rollNo} | Admission No: {student.admissionNo} | ID: {student.studentID}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Main Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          {/* Student Basic Information */}
          <div className="p-4 bg-gray-50 border-b">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Student Information</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-gray-500 text-xs">Student ID</p>
                <p className="font-medium text-gray-800">{student.studentID}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs">UBI ID</p>
                <p className="font-medium text-gray-800">{student.ubiID || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs">APAAR No</p>
                <p className="font-medium text-gray-800">{student.apaarNo || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs">Academic Session</p>
                <p className="font-medium text-gray-800">{student.academicSession || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs">Gender</p>
                <p className="font-medium text-gray-800">{formatGender(student.gender)}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs">Date of Birth</p>
                <p className="font-medium text-gray-800">{student.dob ? new Date(student.dob).toLocaleDateString('en-IN') : '-'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs">Blood Group</p>
                <p className="font-medium text-gray-800">{student.bloodGroup || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs">Class - Section</p>
                <p className="font-medium text-gray-800">{student.class} - {student.section}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs">School Name</p>
                <p className="font-medium text-gray-800 col-span-2">{student.schoolName || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs">RO Name</p>
                <p className="font-medium text-gray-800">{student.roName || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs">State</p>
                <p className="font-medium text-gray-800">{student.stateName || '-'}</p>
              </div>
            </div>
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-4 gap-4 p-4 bg-white border-b">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{presentCount}</p>
              <p className="text-xs text-gray-500">Present</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{absentCount}</p>
              <p className="text-xs text-gray-500">Absent</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-400">{notMarkedCount}</p>
              <p className="text-xs text-gray-500">Not Marked</p>
            </div>
            <div className="text-center">
              <p className={`text-2xl font-bold ${attendancePercent >= 75 ? 'text-green-600' : attendancePercent >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                {attendancePercent}%
              </p>
              <p className="text-xs text-gray-500">Attendance</p>
            </div>
          </div>

          {/* Attendance Calendar/List */}
          <div className="p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Attendance Register</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {attendanceDetails.map((detail, index) => {
                const date = new Date(detail.attendanceDate);
                const dayName = date.toLocaleDateString('en-IN', { weekday: 'short' });
                const dateStr = date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
                
                const statusConfig = {
                  'Present': { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
                  'Absent': { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
                  'Not Marked': { bg: 'bg-gray-100', text: 'text-gray-500', border: 'border-gray-300' }
                };
                const config = statusConfig[detail.attendanceStatus] || statusConfig['Not Marked'];

                return (
                  <div 
                    key={index}
                    className={`p-2 rounded-lg border ${config.bg} ${config.border} flex items-center justify-between`}
                  >
                    <div>
                      <p className="text-xs text-gray-500">{dayName}</p>
                      <p className="text-sm font-medium text-gray-700">{dateStr}</p>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-1 rounded ${config.text}`}>
                      {detail.attendanceStatus === 'Present' ? 'P' : detail.attendanceStatus === 'Absent' ? 'A' : '-'}
                    </span>
                  </div>
                );
              })}
            </div>
            {attendanceDetails.length === 0 && (
              <p className="text-center text-gray-500 py-8">No attendance records found for this period</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
          <button
            onClick={handleViewFullDetails}
            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            View Full Details
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const StudentSectionDetail: React.FC = () => {
  const navigate = useNavigate();
  const { kvId, classNum, section } = useParams<{ kvId: string; classNum: string; section: string }>();
  const [searchParams] = useSearchParams();
  const carouselImages = [img1];

  const kvName = searchParams.get('kvName') || '';
  const streamName = searchParams.get('stream') || '';
  const urlFromDate = searchParams.get('fromDate');
  const urlToDate = searchParams.get('toDate');

  // Get default date range (last 30 days to today)
  const getDefaultDate = (daysAgo: number) => {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return date.toISOString().split('T')[0];
  };

  const [students, setStudents] = useState<Student[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState(urlFromDate || getDefaultDate(30));
  const [toDate, setToDate] = useState(urlToDate || getDefaultDate(0));
  const orderBy: 'firstName' | 'rollNo' = 'firstName';
  const order: 'ASC' | 'DESC' = 'ASC';
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const tableRef = useRef<any>(null);

  // Calculate attendance percentage for a student
  const getAttendanceStats = (student: Student) => {
    const details = student.attendancedetails || [];
    const presentCount = details.filter(a => a.attendanceStatus === 'Present').length;
    const absentCount = details.filter(a => a.attendanceStatus === 'Absent').length;
    const markedDays = presentCount + absentCount;
    const percent = markedDays > 0 ? Math.round((presentCount / markedDays) * 100) : 0;
    return { presentCount, absentCount, markedDays, percent };
  };

  // Load students
  const loadStudents = useCallback(async () => {
    if (!kvId || !classNum || !section) return;

    setIsLoading(true);
    try {
      const response = await attendanceApi.getClassSectionStudents(
        kvId, 
        classNum, 
        section,
        1, // Always use page 1 since DataTables handles pagination
        fromDate,
        toDate,
        orderBy,
        order
      );
      if (response && response.success) {
        setStudents(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Error loading students:', error);
    } finally {
      setIsLoading(false);
    }
  }, [kvId, classNum, section, fromDate, toDate, orderBy, order]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  // Filter students based on search
  const filteredStudents = students.filter(student => {
    const fullName = `${student.firstName} ${student.middleName} ${student.lastName}`.toLowerCase();
    const search = searchQuery.toLowerCase();
    return (
      fullName.includes(search) ||
      student.rollNo?.toString().includes(search) ||
      student.admissionNo?.includes(search)
    );
  });

  // Calculate overall attendance stats
  // const overallStats = useMemo(() => {
  //   let totalPresent = 0;
  //   let totalAbsent = 0;
  //   students.forEach(student => {
  //     const stats = getAttendanceStats(student);
  //     totalPresent += stats.presentCount;
  //     totalAbsent += stats.absentCount;
  //   });
  //   const totalMarked = totalPresent + totalAbsent;
  //   const avgPercent = totalMarked > 0 ? Math.round((totalPresent / totalMarked) * 100) : 0;
  //   return { totalPresent, totalAbsent, avgPercent };
  // }, [students]);

  // Generate full date range (inclusive) from `fromDate` to `toDate`
  const rangeDates = useMemo(() => {
    try {
      const res: string[] = [];
      const start = new Date(fromDate);
      const end = new Date(toDate);
      // Normalize to start of day
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        // push YYYY-MM-DD key (UTC-independent string)
        res.push(new Date(d).toISOString().split('T')[0]);
      }
      return res;
    } catch (err) {
      return [];
    }
  }, [fromDate, toDate]);

  // Prepare DataTable columns
  const tableColumns = useMemo(() => {
    const cols: any[] = [
      {
        title: '<div class="text-xs font-bold text-slate-700">Roll<br/>No</div>',
        data: null,
        className: 'text-center text-sm font-bold text-slate-900',
        render: (data: any, type: any, row: any, meta: any) => meta.row + 1,
        width: '60px'
      },
      {
        title: '<div class="text-xs font-bold text-slate-700">Id<br/>No</div>',
        data: 'rollNo',
        className: 'text-center text-sm font-medium text-slate-700',
        width: '80px'
      },
      {
        title: '<div class="text-xs font-bold text-slate-700">Student Name</div>',
        data: null,
        className: 'text-left',
        render: (data: any) => {
          const fullName = `${data.firstName} ${data.middleName ? data.middleName + ' ' : ''}${data.lastName}`.trim();
          return `<button 
            class="text-sm font-semibold text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer text-left"
            onclick="window.openStudentModal('${data.studentID}')"
            title="Click to view attendance details"
          >${fullName}</button>`;
        },
        width: '200px'
      }
    ];

    // Add date columns
    rangeDates.forEach((dateStr, idx) => {
      const date = new Date(dateStr);
      const day = date.getDate();
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const weekdayName = date.toLocaleDateString('en-IN', { weekday: 'short' });
      
      cols.push({
        title: `<div class="text-xs font-bold text-gray-700">${day}<br/><span class="${isWeekend ? 'text-orange-600 font-semibold' : 'text-gray-500'} text-[10px]">${weekdayName}</span></div>`,
        data: null,
        className: `text-center py-3 ${isWeekend ? 'bg-orange-100/60' : ''}`,
        render: (data: any) => {
          const attendance = (data.attendancedetails || []).find((a: any) => 
            new Date(a.attendanceDate).toISOString().split('T')[0] === dateStr
          );
          if (attendance?.attendanceStatus === 'Present') {
            return '<span class="text-emerald-600 font-bold text-xl">✓</span>';
          } else if (attendance?.attendanceStatus === 'Absent') {
            return '<span class="text-rose-600 font-bold text-xl">✗</span>';
          }
          return '<span class="text-slate-300 text-sm">-</span>';
        },
        width: '40px',
        orderable: false
      });
    });

    // Summary columns
    cols.push(
      {
        title: '<div class="text-xs font-bold text-amber-700">Total<br/>Days</div>',
        data: null,
        className: 'text-center text-sm font-bold text-amber-700 bg-amber-50/40',
        render: () => rangeDates.length,
        width: '70px',
        orderable: false
      },
      {
        title: '<div class="text-xs font-bold text-emerald-700">Present<br/>Count</div>',
        data: null,
        className: 'text-center text-sm font-bold text-emerald-700 bg-emerald-50/40',
        render: (data: any) => {
          const presentCount = (data.attendancedetails || []).filter((a: any) => a.attendanceStatus === 'Present').length;
          return presentCount;
        },
        width: '80px'
      },
      {
        title: '<div class="text-xs font-bold text-rose-700">Absent<br/>Count</div>',
        data: null,
        className: 'text-center text-sm font-bold text-rose-700 bg-rose-50/40',
        render: (data: any) => {
          const absentCount = (data.attendancedetails || []).filter((a: any) => a.attendanceStatus === 'Absent').length;
          return absentCount;
        },
        width: '80px'
      },
      {
        title: '<div class="text-xs font-bold text-indigo-700">Attendance<br/>%</div>',
        data: null,
        className: 'text-center bg-indigo-50/60',
        render: (data: any) => {
          const stats = getAttendanceStats(data);
          const percent = stats.percent;
          const colorClass = 
            percent >= 90 ? 'bg-emerald-500 hover:bg-emerald-600' :
            percent >= 75 ? 'bg-blue-500 hover:bg-blue-600' :
            percent >= 50 ? 'bg-amber-500 hover:bg-amber-600' :
            'bg-rose-500 hover:bg-rose-600';
          
          return `<button 
            class="px-3 py-1.5 rounded-lg font-bold text-sm text-white transition-all duration-200 ${colorClass}"
            onclick="window.viewStudentDetails('${data.studentID}')"
            title="Click to view details"
          >${percent}%</button>`;
        },
        width: '90px'
      }
    );

    return cols;
  }, [rangeDates]);

  // DataTable options
  const tableOptions = useMemo(() => ({
    destroy: true, // Allow reinitialization
    data: filteredStudents,
    columns: tableColumns,
    paging: true,
    pageLength: 25,
    lengthMenu: [[10, 25, 50, 100], [10, 25, 50, 100]],
    searching: false, // We use custom search
    ordering: true,
    info: true,
    autoWidth: false,
    responsive: false,
    scrollX: true,
    scrollCollapse: true,
    fixedColumns: {
      leftColumns: 3,
      rightColumns: 1
    },
    language: {
      emptyTable: searchQuery ? 'No students found matching your search' : 'No students found',
      info: 'Showing _START_ to _END_ of _TOTAL_ students',
      infoEmpty: 'Showing 0 to 0 of 0 students',
      lengthMenu: 'Show _MENU_ students per page',
      paginate: {
        first: 'First',
        last: 'Last',
        next: '›',
        previous: '‹'
      }
    },
    createdRow: function(row: HTMLTableRowElement, data: any, dataIndex: number) {
      // Apply alternating row colors using vanilla JS
      row.classList.add(
        dataIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50',
        'hover:bg-indigo-50',
        'border-b',
        'border-slate-200',
        'transition-all',
        'duration-150'
      );
    }
  }), [filteredStudents, tableColumns, searchQuery]);

  // Setup window function for viewing student details
  useEffect(() => {
    (window as any).viewStudentDetails = (studentId: string) => {
      navigate(`/attendance/register?role=student&studentid=${studentId}&kvId=${kvId}&class=${classNum}&section=${section}`);
    };
    
    // Setup window function for opening student modal
    (window as any).openStudentModal = (studentId: string) => {
      const student = students.find(s => s.studentID === studentId);
      if (student) {
        setSelectedStudent(student);
      }
    };
    
    return () => {
      delete (window as any).viewStudentDetails;
      delete (window as any).openStudentModal;
    };
  }, [kvId, classNum, section, navigate, students]);

  // const handleViewStudent = (studentId: string) => {
  //   navigate(`/attendance/register?role=student&studentid=${studentId}&kvId=${kvId}&class=${classNum}&section=${section}`);
  // };

  const handleDateRangeSubmit = (range: { fromDate: string | null; toDate: string | null }) => {
    if (range.fromDate) setFromDate(range.fromDate);
    if (range.toDate) setToDate(range.toDate);
  };

  // const toggleSort = (column: 'firstName' | 'rollNo') => {
  //   if (orderBy === column) {
  //     setOrder(order === 'ASC' ? 'DESC' : 'ASC');
  //   } else {
  //     setOrderBy(column);
  //     setOrder('ASC');
  //   }
  // };

  // const getGenderBadge = (gender: string) => {
  //   return gender === '1' ? (
  //     <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Male</span>
  //   ) : (
  //     <span className="px-2 py-0.5 bg-pink-100 text-pink-700 rounded-full text-xs font-medium">Female</span>
  //   );
  // };

  // const getAttendanceColor = (percent: number) => {
  //   if (percent >= 90) return 'text-green-600 bg-green-50';
  //   if (percent >= 75) return 'text-blue-600 bg-blue-50';
  //   if (percent >= 50) return 'text-amber-600 bg-amber-50';
  //   return 'text-red-600 bg-red-50';
  // };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="flex-grow flex flex-col items-center justify-center">
          <KVSLoader size={80} />
          <span className="mt-4 text-gray-600">Loading students...</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full max-w-7xl mx-auto px-4 pt-2 sm:pt-3">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#6A1786] flex items-center gap-2">
              <GraduationCap className="w-7 h-7" />
              Class {classNum} - Section {section}
              {streamName && streamName !== 'NA' && (
                <span className={`ml-2 px-3 py-1 rounded-lg text-lg font-medium ${
                  streamName === 'Science' ? 'bg-blue-100 text-blue-700' :
                  streamName === 'Commerce' ? 'bg-green-100 text-green-700' :
                  streamName === 'Humanities' ? 'bg-purple-100 text-purple-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {streamName}
                </span>
              )}
            </h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Building2 className="w-4 h-4" />
                {kvName}
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {students.length} Students
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Date Range Picker */}
            <DateRangePicker
              value={{ fromDate, toDate }}
              onSubmit={handleDateRangeSubmit}
            />
            <button
              onClick={loadStudents}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
              title="Refresh data"
            >
              <RefreshCw className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-purple-100">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Students</p>
                <p className="text-2xl font-bold text-gray-900">{students.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-blue-100">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Boys</p>
                <p className="text-2xl font-bold text-blue-600">
                  {students.filter(s => s.gender === '1').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-pink-100">
                <Users className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Girls</p>
                <p className="text-2xl font-bold text-pink-600">
                  {students.filter(s => s.gender === '2').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-green-100">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Session</p>
                <p className="text-2xl font-bold text-green-600">
                  {students[0]?.academicSession || '2025'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, roll number, or admission number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>

        {/* Attendance Register - Diary Style */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-xl">
          {/* Register Header */}
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Attendance Register</h2>
                <p className="text-sm text-white/80 mt-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(fromDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })} - {new Date(toDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-white/80">Total Days</p>
                <p className="text-3xl font-bold">{rangeDates.length}</p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto p-4">
            <DataTable
              key={`dt-${filteredStudents.length}-${searchQuery}-${fromDate}-${toDate}`}
              ref={tableRef}
              options={tableOptions}
              className="attendance-register-table display nowrap w-full"
            />
          </div>

          {/* Legend */}
          <div className="border-t-2 border-slate-200 bg-gradient-to-r from-slate-50 to-white px-6 py-4">
            <div className="flex items-center justify-center gap-12 text-sm">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <span className="text-emerald-600 font-bold text-xl">✓</span>
                </div>
                <span className="text-slate-700 font-semibold">Present</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center">
                  <span className="text-rose-600 font-bold text-xl">✗</span>
                </div>
                <span className="text-slate-700 font-semibold">Absent</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                  <span className="text-slate-400 font-bold">-</span>
                </div>
                <span className="text-slate-700 font-semibold">Not Marked</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-orange-600" />
                </div>
                <span className="text-slate-700 font-semibold">Weekend</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Attendance Detail Modal */}
      {selectedStudent && (
        <AttendanceModal
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
        />
      )}
    </div>
  );
};

export default StudentSectionDetail;
